import { BillingAccountBlock } from "@/admin/accounts/BillingAccountBlock.tsx";
import { DateViewer, Extra } from "@/admin/AdminTable.tsx";
import { gf, type GqlFieldsOf } from "@/admin/FieldConfig.tsx";
import {
    EnumComponentFilter,
    EnumFilter,
    ExactNumberFilter,
    NumberRangeFilter,
    TextFilter,
    UnixDateRangeFilter,
} from "@/admin/filters.tsx";
import { gql } from "@/admin/graphql.gen/gql.ts";
import { InvoiceOrderFields, OrderDirection, type GetInvoiceListQuery } from "@/admin/graphql.gen/graphql.ts";
import { extractData } from "@/admin/graphql/extractData.tsx";
import { GraphqlTable } from "@/admin/GraphqlTable.tsx";
import { InvoiceDetailView } from "@/admin/reports/InvoiceDetailView.tsx";
import { InvoiceToolbar } from "@/admin/reports/InvoiceToolbar.tsx";
import { DocumentTypeLabel, documentTypes, ReportType, reportTypes } from "@/admin/reports/ReportComponents.tsx";
import { UpdateInvoiceStatusBlock } from "@/admin/reports/UpdateInvoiceStatusBlock.tsx";
import {
    baField,
    baIdField,
    createdAtField,
    currencyField,
    idField,
    numberField,
    userIdField,
} from "@/admin/table_fields/standardFields.tsx";
import { whenNotNull } from "@/admin/table_fields/whenNotNull.tsx";
import { CurrencyBalance } from "@/components/l10n/Currency.tsx";
import { Percent } from "@/components/l10n/Percent.tsx";
import { VatText, VatTextPercent } from "@/components/VatText.tsx";
import { DownloadPdf } from "@/modules/billing/invoice/DownloadPdf.tsx";
import { hasPdfDownload } from "@/modules/billing/invoice/invoiceLogic.tsx";
import { ReportStatus } from "@/modules/billing/ReportStatus.tsx";
import { useQuery } from "@apollo/client/react";
import { apiUnixToDate } from "@warrenio/api-spec/conversion";
import type { DocumentTypes, InvoiceStatus, InvoiceTypes } from "@warrenio/api-spec/spec.oats.gen";
import { InvoiceStatusEnum } from "@warrenio/api-spec/spec/billingEnums";
import { keys } from "remeda";

const GET_INVOICES = gql(/* GraphQL */ `
    query GetInvoiceList(
        $limit: Int
        $page: Int
        $orderField: InvoiceOrderFields
        $orderDir: OrderDirection
        $search: String
        $filters: [InvoiceFilter!]
    ) {
        admin_invoice_list(
            limit: $limit
            page: $page
            orderField: $orderField
            orderDir: $orderDir
            search: $search
            filters: $filters
        ) {
            items {
                id
                padded_id
                document_number
                document_type
                # name
                period_start
                period_end
                created
                status
                billing_account_id
                # account_snapshot
                due_date
                vat_percentage
                discount_percentage
                type
                precalc_subtotal
                precalc_discount_amount
                precalc_credit
                precalc_total_before_tax
                precalc_vat_tax
                precalc_total
                billing_account {
                    allow_debt
                    user_id
                    ...BillingAccountBlock
                }
                totals {
                    subtotal_resource_usage
                    subtotal
                    discount_amount
                    credit
                    total_before_tax
                    vat_tax
                    total
                }
            }
            paging {
                total
            }
        }
    }
`);

export type GQInvoiceItem = NonNullable<GetInvoiceListQuery["admin_invoice_list"]["items"]>[number];

function WithPercent({ value, percent }: { value: number; percent: number | null | undefined }) {
    return <Extra value={<CurrencyBalance value={value} />}>{percent ? <Percent value={percent} /> : null}</Extra>;
}

const userFields: GqlFieldsOf<GQInvoiceItem, InvoiceOrderFields> = [
    gf({
        ...idField,
        id: "id",
        title: "ID",
        get: (a) => a.id,
        order: InvoiceOrderFields.Id,
        filter: ExactNumberFilter,
        hidden: true,
    }),
    gf({
        id: "document_number",
        title: "Doc No.",
        get: (a) => a.document_number,
        order: InvoiceOrderFields.DocumentNumber,
        filter: TextFilter,
        copyable: true,
    }),
    gf({
        ...baIdField,
        get: (a) => a.billing_account_id,
        order: InvoiceOrderFields.BillingAccountId,
        filter: ExactNumberFilter,
    }),
    gf({
        ...userIdField,
        get: (a) => a.billing_account?.user_id,
        order: InvoiceOrderFields.BillingAccountUserId,
        hidden: true,
    }),
    gf({
        ...baField,
        get: (a) => a.billing_account,
        render: BillingAccountBlock,
    }),
    gf({
        ...createdAtField,
        get: (a) => a.created && apiUnixToDate(a.created),
        order: InvoiceOrderFields.Created,
    }),
    gf({
        id: "report_type",
        title: "Report Type",
        get: (a) => a.type as InvoiceTypes,
        render: ReportType,
        order: InvoiceOrderFields.Type,
        filter: () => <EnumComponentFilter values={keys(reportTypes)} component={ReportType} />,
    }),
    gf({
        id: "period_start",
        title: "Period Start",
        get: (a) => apiUnixToDate(a.period_start),
        order: InvoiceOrderFields.PeriodStart,
        filter: UnixDateRangeFilter,
    }),
    gf({
        id: "period_end",
        title: "Period End",
        get: (a) => apiUnixToDate(a.period_end),
        render: ({ value, item }) =>
            item.period_start === item.period_end ? (
                <div className="color-muted">
                    <DateViewer value={value} />
                </div>
            ) : (
                <DateViewer value={value} />
            ),
        order: InvoiceOrderFields.PeriodEnd,
        filter: UnixDateRangeFilter,
    }),
    gf({
        ...currencyField,
        id: "subtotal_resource_usage",
        title: "Resource Usage\nSubtotal",
        get: (a) => a.totals?.subtotal_resource_usage,
    }),
    gf({
        ...currencyField,
        id: "subtotal",
        title: "Subtotal",
        get: (a) => a.totals?.subtotal,
        order: InvoiceOrderFields.PrecalcSubtotal,
        filter: NumberRangeFilter,
    }),
    gf({
        ...currencyField,
        id: "discount",
        title: "Discount",
        get: (a) => a.precalc_discount_amount,
        render: ({ value, item }) => <WithPercent value={value} percent={item.discount_percentage} />,
        order: InvoiceOrderFields.PrecalcDiscountAmount,
        filter: NumberRangeFilter,
    }),
    gf({
        ...numberField,
        id: "discount_percentage",
        title: "Discount %",
        get: (a) => a.discount_percentage,
        render: whenNotNull(Percent),
        order: InvoiceOrderFields.DiscountPercentage,
        filter: NumberRangeFilter,
        hidden: true,
    }),
    gf({
        ...currencyField,
        id: "credit",
        title: "Credit",
        get: (a) => a.precalc_credit,
        order: InvoiceOrderFields.PrecalcCredit,
        filter: NumberRangeFilter,
    }),
    gf({
        ...numberField,
        id: "vat",
        title: <VatText />,
        get: (a) => a.precalc_vat_tax,
        render: ({ value, item }) => <WithPercent value={value} percent={item.vat_percentage} />,
        order: InvoiceOrderFields.PrecalcVatTax,
        filter: NumberRangeFilter,
    }),
    gf({
        ...numberField,
        id: "vat_percentage",
        title: <VatTextPercent />,
        get: (a) => a.vat_percentage,
        render: whenNotNull(Percent),
        order: InvoiceOrderFields.VatPercentage,
        filter: NumberRangeFilter,
        hidden: true,
    }),
    gf({
        ...currencyField,
        id: "total",
        title: "Total",
        get: (a) => a.precalc_total,
        order: InvoiceOrderFields.PrecalcTotal,
        filter: NumberRangeFilter,
    }),
    gf({
        id: "document_type",
        title: "Doc Type",
        get: (a) => a.document_type as DocumentTypes | null,
        render: DocumentTypeLabel,
        order: InvoiceOrderFields.DocumentType,
        filter: () => <EnumComponentFilter values={keys(documentTypes)} component={DocumentTypeLabel} />,
    }),
    gf({
        id: "status",
        title: "Status",
        get: (a) => a.status! as InvoiceStatus,
        render: ReportStatus,
        order: InvoiceOrderFields.Status,
        filter: () => (
            <EnumFilter
                items={Object.values(InvoiceStatusEnum).map((value) => ({
                    value: String(value),
                    label: <ReportStatus value={value} />,
                }))}
            />
        ),
    }),
];

export function ReportsTable() {
    return (
        <GraphqlTable
            title="Invoices & Reports"
            fields={userFields}
            defaults={{ orderField: InvoiceOrderFields.Created, orderDir: OrderDirection.Desc }}
            getId={(item) => item.id}
            useQuery={(variables) => {
                // eslint-disable-next-line react-hooks/rules-of-hooks
                const q = useQuery(GET_INVOICES, { variables });
                return extractData(q, (d) => d.admin_invoice_list);
            }}
            actionButtons={(item) => (
                <>
                    <UpdateInvoiceStatusBlock item={item} inTable />
                    {hasPdfDownload(item) && <DownloadPdf item={item} inTable isAdmin />}
                </>
            )}
            renderDetailToolbar={(item) => <InvoiceToolbar item={item} />}
            renderDetail={(item) => <InvoiceDetailView item={item} />}
        />
    );
}
